import { useEffect, useRef, useState } from "react";
import { useLocation, Navigate } from "react-router-dom";
import {
  Lock, Upload, FileText, Download, Trash2, Loader2, LogOut, Check, ShieldCheck,
  MessageSquare, Send, RefreshCw, History, Camera,
} from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "@/lib/motion";
import { useVisibleInterval } from "@/lib/useVisibleInterval";
import { QRCodeSVG } from "qrcode.react";
import { useAuth } from "@/lib/AuthContext";
import ConfirmDetails from "@/components/portal/ConfirmDetails";
import SatisfactionMeter from "@/components/portal/SatisfactionMeter";
import {
  fetchDocuments, uploadDocument, downloadDocument, deleteDocument,
  fetchChecklist, saveChecklist, formatApiError,
  mfaVerify, mfaEnroll, mfaConfirm, mfaDisable,
  fetchMessages, sendMessage, fetchActivity, fetchStatus, changePassword,
} from "@/lib/api";
import { PORTAL_BASICS, INCOME_TYPES } from "@/data/content";
import PortalInvoices from "@/components/PortalInvoices";

const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden="true">
    <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z" />
    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z" />
    <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z" />
    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z" />
  </svg>
);

const inputCls =
  "w-full border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";

const fmtSize = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function AuthView() {
  const { login, register, googleLogin, setUser } = useAuth();
  const [mode, setMode] = useState(() =>
    new URLSearchParams(window.location.search).has("signup") ? "register" : "signin",
  );
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mfaStep, setMfaStep] = useState(false);
  const [code, setCode] = useState("");
  const [tosAccepted, setTosAccepted] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (mode === "signin" && !tosAccepted) {
      setError("Please accept the Terms of Service to sign in.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      if (mode === "signin") {
        const res = await login(form.email, form.password, tosAccepted);
        if (res?.mfa_required) {
          setMfaStep(true);
          return;
        }
      } else {
        await register(form.name, form.email, form.password);
      }
      toast.success(mode === "signin" ? "Welcome back." : "Account created — welcome.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const userData = await mfaVerify(code);
      setUser(userData);
      toast.success("Welcome back.");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Reveal>
      <div className="glass border border-steel-border p-10">
        <div className="flex items-center justify-between">
          <div className="flex h-11 w-11 items-center justify-center border border-steel-border">
            <Lock className="h-5 w-5 text-steel-brass" />
          </div>
          <div className="flex border border-steel-border" data-testid="portal-auth-tabs">
            {["signin", "register"].map((m) => (
              <button
                key={m}
                data-testid={`portal-tab-${m}`}
                onClick={() => { setMode(m); setError(""); }}
                className={`px-4 py-2 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors duration-300 ${
                  mode === m ? "bg-steel-accent text-white" : "text-steel-muted hover:text-steel-text"
                }`}
              >
                {m === "signin" ? "Sign In" : "Create Account"}
              </button>
            ))}
          </div>
        </div>

        {mfaStep ? (
          <form onSubmit={onVerify} className="mt-8 space-y-5" data-testid="portal-mfa-form">
            <div>
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">
                Authenticator code
              </label>
              <input
                data-testid="portal-mfa-code-input"
                autoComplete="one-time-code"
                maxLength={9}
                required
                value={code}
                onChange={(e) => setCode(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))}
                className={`${inputCls} text-center font-mono text-2xl tracking-[0.4em]`}
                placeholder="000000"
              />
              <p className="mt-2 text-xs text-steel-muted">
                Enter the 6-digit code from your authenticator app — or a recovery code (xxxx-xxxx).
              </p>
            </div>
            {error && (
              <p data-testid="portal-mfa-error" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
                {error}
              </p>
            )}
            <button
              data-testid="portal-mfa-verify-btn"
              type="submit"
              disabled={loading || code.length < 6}
              className="flex w-full items-center justify-center gap-2 bg-steel-text px-8 py-4 text-sm font-semibold text-steel-bg transition-colors duration-300 hover:bg-zinc-300 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify & Sign In"}
            </button>
          </form>
        ) : (
        <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="portal-auth-form">
          {mode === "register" && (
            <div>
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Full name</label>
              <input
                data-testid="portal-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputCls}
                placeholder="Jane Citizen"
              />
            </div>
          )}
          <div>
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">Email</label>
            <input
              data-testid="portal-email-input"
              type="email"
              required
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className={inputCls}
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-steel-muted">
              Password {mode === "register" && <span className="text-steel-muted/70">(8+ characters)</span>}
            </label>
            <input
              data-testid="portal-password-input"
              type="password"
              required
              minLength={mode === "register" ? 8 : 1}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className={inputCls}
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p data-testid="portal-auth-error" className="border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300">
              {error}
            </p>
          )}
          {mode === "signin" && (
            <label className="flex cursor-pointer items-start gap-3 text-xs leading-relaxed text-steel-secondary" data-testid="portal-tos-label">
              <input
                data-testid="portal-tos-checkbox"
                type="checkbox"
                checked={tosAccepted}
                onChange={(e) => setTosAccepted(e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 accent-steel-brass"
              />
              <span>
                I agree to the{" "}
                <a href="/terms" target="_blank" rel="noreferrer" className="text-steel-brass underline underline-offset-2">
                  Terms of Service
                </a>{" "}
                and consent to the secure handling of my tax documents. Your device and sign-in are recorded for your protection.
              </span>
            </label>
          )}
          <button
            data-testid="portal-auth-submit"
            type="submit"
            disabled={loading || (mode === "signin" && !tosAccepted)}
            className="flex w-full items-center justify-center gap-2 bg-steel-text px-8 py-4 text-sm font-semibold text-steel-bg transition-colors duration-300 hover:bg-zinc-300 disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "signin" ? "Sign In" : "Create Account"}
          </button>
        </form>
        )}

        <div className="my-6 flex items-center gap-4">
          <div className="hairline-glow flex-1" />
          <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">or</span>
          <div className="hairline-glow flex-1" />
        </div>

        <button
          data-testid="portal-google-btn"
          onClick={() => {
            if (mode === "signin" && !tosAccepted) {
              setError("Please accept the Terms of Service to continue.");
              return;
            }
            localStorage.setItem("steele_tos_accepted", "1");
            googleLogin();
          }}
          disabled={mode === "signin" && !tosAccepted}
          className="flex w-full items-center justify-center gap-3 border border-steel-border px-8 py-4 text-sm font-medium text-steel-text transition-colors duration-300 hover:bg-steel-surface disabled:opacity-50"
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <p className="mt-6 text-xs leading-relaxed text-steel-muted">
          Sessions are protected with secure, httpOnly cookies. Sensitive documents belong here — never in email.
        </p>
      </div>
    </Reveal>
  );
}

function Dashboard({ user }) {
  const [docs, setDocs] = useState(null);
  const [checked, setChecked] = useState({});
  const [incomeTypes, setIncomeTypes] = useState([]);
  const [requested, setRequested] = useState([]);
  const [status, setStatus] = useState("gathering");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [activityKey, setActivityKey] = useState(0);
  const fileInput = useRef(null);
  const photoInput = useRef(null);

  const loadDocs = () => fetchDocuments().then((d) => setDocs(d.documents)).catch(() => setDocs([]));

  useEffect(() => {
    loadDocs();
    fetchChecklist()
      .then((d) => {
        setChecked(d.checked || {});
        setIncomeTypes(d.income_types || []);
        setRequested(d.requested || []);
      })
      .catch(() => {});
    fetchStatus().then((d) => setStatus(d.stage)).catch(() => {});
  }, []);

  const onUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      await uploadDocument(file);
      setFile(null);
      if (fileInput.current) fileInput.current.value = "";
      toast.success("Uploaded securely.");
      loadDocs();
      setActivityKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (doc) => {
    try {
      await deleteDocument(doc.id);
      toast.success("Document removed.");
      setDocs((d) => d.filter((x) => x.id !== doc.id));
      setActivityKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleCheck = (key) => {
    const next = { ...checked, [key]: !checked[key] };
    setChecked(next);
    saveChecklist(next, incomeTypes).catch(() => {});
  };

  const toggleIncome = (id) => {
    const next = incomeTypes.includes(id) ? incomeTypes.filter((x) => x !== id) : [...incomeTypes, id];
    setIncomeTypes(next);
    saveChecklist(checked, next).catch(() => {});
  };

  const itemFileInput = useRef(null);
  const pendingKey = useRef(null);

  const pickFileFor = (key) => {
    pendingKey.current = key;
    itemFileInput.current?.click();
  };

  const uploadForItem = async (key, f) => {
    try {
      await uploadDocument(f, key);
      toast.success("Uploaded — item checked off.");
      const next = { ...checked, [key]: true };
      setChecked(next);
      saveChecklist(next, incomeTypes).catch(() => {});
      loadDocs();
      setActivityKey((k) => k + 1);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const ChecklistRow = ({ rowKey, text, accent = false }) => {
    const on = !!checked[rowKey];
    return (
      <div className="flex w-full items-start gap-2.5">
        <button
          type="button"
          data-testid={`portal-check-${rowKey}`}
          onClick={() => toggleCheck(rowKey)}
          className="group flex flex-1 items-start gap-3 text-left"
        >
          <span
            className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center border transition-colors duration-300 ${
              on
                ? accent
                  ? "border-amber-400 bg-amber-400/20"
                  : "border-steel-brass bg-steel-brass/20"
                : accent
                  ? "border-amber-400/50 group-hover:border-amber-400"
                  : "border-steel-border group-hover:border-steel-muted"
            }`}
          >
            {on && <Check className={`h-3.5 w-3.5 ${accent ? "text-amber-400" : "text-steel-brass"}`} />}
          </span>
          <span className={`text-sm leading-relaxed transition-colors duration-300 ${on ? "text-steel-muted line-through" : "text-steel-secondary"}`}>
            {text}
          </span>
        </button>
        <button
          type="button"
          data-testid={`portal-upload-for-${rowKey}`}
          onClick={() => pickFileFor(rowKey)}
          title="Upload a file for this item"
          aria-label="Upload a file for this item"
          className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center border border-steel-border text-steel-muted transition-colors hover:border-steel-brass hover:text-steel-brass"
        >
          <Upload className="h-3 w-3" />
        </button>
      </div>
    );
  };

  const visibleGroups = [
    { id: "basic", title: "Everyone needs", items: PORTAL_BASICS },
    ...INCOME_TYPES.filter((t) => incomeTypes.includes(t.id)).map((t) => ({ id: t.id, title: t.label, items: t.docs })),
  ];
  const visibleKeys = visibleGroups.flatMap((g) => g.items.map((_, i) => `${g.id}-${i}`));
  const requestedKeys = requested.map((r) => `req-${r.id}`);
  const totalItems = visibleKeys.length + requestedKeys.length;
  const doneItems =
    visibleKeys.filter((k) => checked[k]).length + requestedKeys.filter((k) => checked[k]).length;
  const stageIdx = Math.max(0, RETURN_STAGES.findIndex((s) => s.id === status));
  const pct = Math.round(((stageIdx + 1) / RETURN_STAGES.length) * 100);
  const stage = RETURN_STAGES[stageIdx].label;

  return (
    <div data-testid="portal-dashboard" className="grid grid-cols-1 gap-10 lg:grid-cols-12">
      <div className="space-y-10 lg:col-span-7">
        <Reveal>
          <div className="glass border border-steel-border p-8" data-testid="portal-progress-card">
            <div className="flex items-end justify-between gap-6">
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-steel-muted">
                  Your return status — step {stageIdx + 1} of {RETURN_STAGES.length}
                </p>
                <p data-testid="portal-progress-pct" className="neon-text mt-2 font-display text-5xl font-medium text-steel-brass">
                  {pct}%
                </p>
                <p data-testid="portal-progress-stage" className="mt-2 text-sm text-steel-secondary">{stage}</p>
              </div>
              <div className="text-right font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">
                <p>
                  {doneItems} / {totalItems} checklist items
                </p>
                <p className="mt-1.5">{docs?.length ?? 0} documents uploaded</p>
              </div>
            </div>
            <div className="mt-6 h-2 w-full bg-steel-elevated">
              <div
                data-testid="portal-progress-bar"
                className="h-2 bg-steel-brass shadow-[0_0_16px_rgba(168,85,247,0.6)] transition-all duration-700"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </Reveal>
        <Reveal>
          <div className="glass border border-steel-border p-8">
            <div className="flex items-center gap-4">
              <div className="flex h-11 w-11 items-center justify-center border border-steel-border">
                <Upload className="h-5 w-5 text-steel-brass" />
              </div>
              <div>
                <h3 className="font-display text-xl font-medium text-steel-text">Upload documents</h3>
                <p className="text-xs text-steel-muted">PDF, JPG, PNG, WEBP, HEIC, CSV, TXT or ZIP · max 25 MB</p>
              </div>
            </div>
            <div className="mt-6 flex flex-col gap-4 sm:flex-row">
              <input
                ref={fileInput}
                data-testid="portal-upload-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.csv,.txt,.zip"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full border border-dashed border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-secondary file:mr-4 file:border-0 file:bg-steel-accent file:px-4 file:py-1.5 file:text-xs file:font-semibold file:text-white"
              />
              <button
                data-testid="portal-upload-btn"
                onClick={onUpload}
                disabled={!file || uploading}
                className="flex shrink-0 items-center justify-center gap-2 bg-steel-accent px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.30)] transition-[background-color,box-shadow] duration-300 hover:bg-steel-accentHover disabled:opacity-50"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upload"}
              </button>
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                data-testid="portal-photo-btn"
                type="button"
                onClick={() => photoInput.current?.click()}
                className="flex items-center gap-2 border border-steel-border px-4 py-2.5 text-xs font-medium text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass"
              >
                <Camera className="h-4 w-4" /> Take a photo instead
              </button>
              <input
                ref={photoInput}
                data-testid="portal-photo-input"
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <input
                ref={itemFileInput}
                data-testid="portal-item-upload-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.csv,.txt,.zip"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f && pendingKey.current) uploadForItem(pendingKey.current, f);
                  e.target.value = "";
                }}
              />
              <span className="text-xs text-steel-muted">On mobile this opens your camera.</span>
            </div>
            {file && (
              <p data-testid="portal-selected-file" className="mt-3 font-mono text-xs text-steel-brass">
                Selected: {file.name}
              </p>
            )}
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="border border-steel-border bg-steel-surface p-8">
            <h3 className="font-display text-xl font-medium text-steel-text">Your documents</h3>
            <div className="mt-6" data-testid="portal-documents-list">
              {docs === null ? (
                <div className="flex items-center gap-3 text-sm text-steel-muted">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : docs.length === 0 ? (
                <div className="flex flex-col items-start gap-3 border border-dashed border-steel-border p-8">
                  <FileText className="h-5 w-5 text-steel-muted" />
                  <p className="text-sm text-steel-secondary">Nothing uploaded yet. Your W-2s, 1099s and records belong here.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {docs.map((doc) => (
                    <div
                      key={doc.id}
                      data-testid={`portal-doc-${doc.id}`}
                      className="flex items-center justify-between gap-4 border border-steel-border bg-steel-elevated px-5 py-4"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-steel-text">{doc.original_filename}</p>
                        <p className="mt-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-steel-muted">
                          {fmtSize(doc.size)} · {new Date(doc.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <button
                          data-testid={`portal-doc-download-${doc.id}`}
                          onClick={() => downloadDocument(doc)}
                          className="flex h-9 w-9 items-center justify-center border border-steel-border text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass"
                          aria-label="Download"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          data-testid={`portal-doc-delete-${doc.id}`}
                          onClick={() => onDelete(doc)}
                          className="flex h-9 w-9 items-center justify-center border border-steel-border text-steel-secondary transition-colors hover:border-red-400 hover:text-red-400"
                          aria-label="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.16}>
          <SecurityCard user={user} />
        </Reveal>

        <Reveal delay={0.18}>
          <PortalInvoices />
        </Reveal>

        <Reveal delay={0.2}>
          <ChatCard onActivity={() => setActivityKey((k) => k + 1)} />
        </Reveal>
      </div>

      <div className="lg:col-span-5">
        <Reveal>
          <SatisfactionMeter />
        </Reveal>

        <Reveal delay={0.12}>
          <div className="border border-steel-border bg-steel-surface p-8">
            <h3 className="font-display text-xl font-medium text-steel-text">Document checklist</h3>
            <p className="mt-2 text-xs leading-relaxed text-steel-muted">
              First tell us what types of income you have — your checklist builds itself from that. Check an
              item off, or tap the upload icon to attach the document directly.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="portal-income-types">
              {INCOME_TYPES.map((t) => {
                const on = incomeTypes.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    data-testid={`income-type-${t.id}`}
                    onClick={() => toggleIncome(t.id)}
                    className={`border px-3 py-1.5 text-xs transition-colors duration-300 ${
                      on
                        ? "border-steel-accent bg-steel-accent/15 text-steel-text shadow-[0_0_14px_rgba(124,58,237,0.25)]"
                        : "border-steel-border text-steel-secondary hover:border-steel-muted hover:text-steel-text"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            {incomeTypes.length === 0 && (
              <p
                data-testid="portal-checklist-hint"
                className="mt-6 border border-dashed border-steel-border p-4 text-xs leading-relaxed text-steel-muted"
              >
                Select your income types above and the exact documents you need appear below — nothing more,
                nothing less.
              </p>
            )}
            <div className="mt-8 space-y-8" data-testid="portal-checklist">
              {requested.length > 0 && (
                <div data-testid="portal-requested-group">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">
                    Requested by Steele Industries
                  </p>
                  <ul className="mt-3 space-y-2.5">
                    {requested.map((item) => (
                      <li key={`req-${item.id}`}>
                        <ChecklistRow rowKey={`req-${item.id}`} text={item.text} accent />
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {visibleGroups.map((g) => (
                <div key={g.id}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-brass">{g.title}</p>
                  <ul className="mt-3 space-y-2.5">
                    {g.items.map((item, i) => (
                      <li key={`${g.id}-${i}`}>
                        <ChecklistRow rowKey={`${g.id}-${i}`} text={item} />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.24}>
          <ActivityCard refreshKey={activityKey} />
        </Reveal>
      </div>
    </div>
  );
}

const RETURN_STAGES = [
  { id: "gathering", label: "Gathering documents" },
  { id: "review", label: "Under review" },
  { id: "preparation", label: "Return in preparation" },
  { id: "client-review", label: "Ready for your review" },
  { id: "filed", label: "Filed" },
];

const ACTION_LABELS = {
  account_created: "Account",
  login: "Sign-in",
  upload: "Upload",
  delete: "Deletion",
  message: "Message",
  profile_updated: "Details updated",
  estimate_sent: "Estimate sent",
  password_changed: "Password changed",
  status: "Status",
  request: "Request",
};

function ChatCard({ onActivity }) {
  const [messages, setMessages] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);
  const lastCount = useRef(0);

  const load = () => fetchMessages().then((d) => setMessages(d.messages)).catch(() => setMessages([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useVisibleInterval(load, 20000);

  useEffect(() => {
    if (messages && messages.length > lastCount.current) {
      const el = threadRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    lastCount.current = messages ? messages.length : 0;
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      await sendMessage(body.trim());
      setBody("");
      load();
      onActivity?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="border border-steel-border bg-steel-surface p-8" data-testid="portal-chat">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-steel-border">
            <MessageSquare className="h-5 w-5 text-steel-brass" />
          </div>
          <div>
            <h3 className="font-display text-xl font-medium text-steel-text">Messages</h3>
            <p className="text-xs text-steel-muted">A direct, timestamped line to your preparer.</p>
          </div>
        </div>
        <button
          data-testid="portal-chat-refresh"
          onClick={load}
          className="flex h-9 w-9 items-center justify-center border border-steel-border text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass"
          aria-label="Refresh messages"
        >
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>
      <div ref={threadRef} className="mt-6 max-h-80 space-y-3 overflow-y-auto pr-1" data-testid="portal-chat-thread">
        {messages === null ? (
          <div className="flex items-center gap-3 text-sm text-steel-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : messages.length === 0 ? (
          <p className="border border-dashed border-steel-border p-5 text-sm text-steel-secondary">
            No messages yet — say hello. Every message is timestamped and kept with your file.
          </p>
        ) : (
          messages.map((m) => (
            <div
              key={m.id}
              className={`max-w-[85%] border px-4 py-3 ${
                m.sender === "client"
                  ? "ml-auto border-steel-accent/40 bg-steel-accent/10"
                  : "border-steel-border bg-steel-elevated"
              }`}
            >
              <p className="text-sm leading-relaxed text-steel-text">{m.body}</p>
              <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.15em] text-steel-muted">
                {m.sender === "client" ? "You" : m.sender_name || "Steele Industries"} ·{" "}
                {new Date(m.created_at).toLocaleString()}
              </p>
            </div>
          ))
        )}
      </div>
      <form onSubmit={send} className="mt-4 flex gap-2">
        <input
          data-testid="portal-chat-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          className={inputCls}
          placeholder="Write a message…"
          maxLength={2000}
        />
        <button
          data-testid="portal-chat-send"
          type="submit"
          disabled={sending || !body.trim()}
          className="flex shrink-0 items-center gap-2 bg-steel-accent px-5 py-3 text-sm font-semibold text-white shadow-[0_0_18px_rgba(168,85,247,0.3)] transition-[background-color,box-shadow] duration-300 hover:bg-steel-accentHover disabled:opacity-50"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}

function ActivityCard({ refreshKey = 0 }) {
  const [events, setEvents] = useState(null);
  const load = () => fetchActivity().then((d) => setEvents(d.activity)).catch(() => setEvents([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);
  useVisibleInterval(load, 20000);

  return (
    <div className="border border-steel-border bg-steel-surface p-8" data-testid="portal-activity">
      <div className="flex items-center gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-steel-border">
          <History className="h-5 w-5 text-steel-brass" />
        </div>
        <div>
          <h3 className="font-display text-xl font-medium text-steel-text">Activity history</h3>
          <p className="text-xs text-steel-muted">Uploads, deletions, sign-ins and messages — timestamped.</p>
        </div>
      </div>
      <div className="mt-6 max-h-72 space-y-0 overflow-y-auto pr-1" data-testid="portal-activity-list">
        {events === null ? (
          <div className="flex items-center gap-3 text-sm text-steel-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : events.length === 0 ? (
          <p className="text-sm text-steel-secondary">Nothing yet — activity appears here as it happens.</p>
        ) : (
          events.map((ev) => (
            <div key={ev.id} className="flex items-start gap-3 border-b border-dotted border-steel-border py-3 last:border-0">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-steel-brass" />
              <div className="min-w-0">
                <p className="text-sm text-steel-text">{ev.detail || ev.action}</p>
                <p className="mt-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-steel-muted">
                  {ACTION_LABELS[ev.action] || ev.action} · {new Date(ev.created_at).toLocaleString()}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SecurityCard({ user }) {
  const { checkAuth } = useAuth();
  const [uri, setUri] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);

  const secret = uri ? new URLSearchParams(uri.split("?")[1] || "").get("secret") : "";

  const startEnroll = async () => {
    setBusy(true);
    try {
      const d = await mfaEnroll();
      setUri(d.provisioning_uri);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    setBusy(true);
    try {
      const d = await mfaConfirm(code);
      setRecoveryCodes(d.recovery_codes || []);
      toast.success("Two-factor authentication enabled.");
      setUri("");
      setCode("");
      checkAuth();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await mfaDisable(code, password);
      toast.success("Two-factor authentication disabled.");
      setShowDisable(false);
      setCode("");
      setPassword("");
      checkAuth();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-steel-border bg-steel-surface p-8" data-testid="portal-security-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-steel-border">
            <ShieldCheck className="h-5 w-5 text-steel-brass" />
          </div>
          <div>
            <h3 className="font-display text-xl font-medium text-steel-text">Two-factor authentication</h3>
            <p className="text-xs text-steel-muted">Google Authenticator, Authy or any TOTP app.</p>
          </div>
        </div>
        <span
          data-testid="mfa-status-badge"
          className={`border px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] ${
            user.totp_enabled
              ? "border-steel-brass/60 text-steel-brass shadow-[0_0_14px_rgba(168,85,247,0.25)]"
              : "border-steel-border text-steel-muted"
          }`}
        >
          {user.totp_enabled ? "On" : "Off"}
        </span>
      </div>

      {!user.totp_enabled && !uri && (
        <div className="mt-6">
          <p className="text-sm leading-relaxed text-steel-secondary">
            Require a 6-digit code from your authenticator app at every sign-in — even if a password leaks,
            your documents stay locked.
          </p>
          <button
            data-testid="mfa-enroll-btn"
            onClick={startEnroll}
            disabled={busy}
            className="mt-5 flex items-center gap-2 bg-steel-accent px-6 py-3 text-sm font-semibold text-white shadow-[0_0_20px_rgba(168,85,247,0.30)] transition-[background-color,box-shadow] duration-300 hover:bg-steel-accentHover disabled:opacity-60"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set Up Two-Factor"}
          </button>
        </div>
      )}

      {uri && (
        <div className="mt-6 space-y-5" data-testid="mfa-enroll-panel">
          <p className="text-sm text-steel-secondary">1. Scan with your authenticator app:</p>
          <div className="inline-block bg-white p-4">
            <QRCodeSVG value={uri} size={180} />
          </div>
          <p className="text-xs text-steel-muted">
            Can't scan? Enter this key manually: <span className="font-mono text-steel-brass">{secret}</span>
          </p>
          <p className="text-sm text-steel-secondary">2. Enter the 6-digit code to confirm:</p>
          <div className="flex gap-3">
            <input
              data-testid="mfa-confirm-code-input"
              inputMode="numeric"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className={`${inputCls} w-40 text-center font-mono text-xl tracking-[0.4em]`}
              placeholder="000000"
            />
            <button
              data-testid="mfa-confirm-btn"
              onClick={confirmEnroll}
              disabled={busy || code.length !== 6}
              className="flex items-center gap-2 bg-steel-text px-6 py-3 text-sm font-semibold text-steel-bg transition-colors hover:bg-zinc-300 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </button>
          </div>
        </div>
      )}

      {recoveryCodes.length > 0 && (
        <div className="mt-6 border border-steel-brass/40 bg-steel-elevated p-6" data-testid="mfa-recovery-codes">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-brass">
            Recovery codes — shown once
          </p>
          <p className="mt-2 text-xs leading-relaxed text-steel-secondary">
            Each code works once if you lose your phone. Store them somewhere safe — they are never shown
            again.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2 font-mono text-sm text-steel-text sm:grid-cols-4">
            {recoveryCodes.map((c) => (
              <span key={c} className="border border-steel-border px-3 py-2 text-center">
                {c}
              </span>
            ))}
          </div>
          <button
            data-testid="mfa-copy-codes-btn"
            onClick={() => {
              navigator.clipboard.writeText(recoveryCodes.join("\n"));
              toast.success("Recovery codes copied.");
            }}
            className="mt-4 border border-steel-border px-4 py-2 text-xs font-medium text-steel-secondary transition-colors hover:text-steel-text"
          >
            Copy codes
          </button>
        </div>
      )}

      {user.totp_enabled && (
        <div className="mt-6">
          <p className="text-sm text-steel-secondary">Every sign-in requires your authenticator code.</p>
          {!showDisable ? (
            <button
              data-testid="mfa-show-disable-btn"
              onClick={() => setShowDisable(true)}
              className="mt-4 text-sm font-medium text-steel-muted transition-colors hover:text-red-400"
            >
              Disable two-factor
            </button>
          ) : (
            <div className="mt-5 space-y-3" data-testid="mfa-disable-panel">
              {user.auth_provider === "password" && (
                <input
                  data-testid="mfa-disable-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputCls}
                  placeholder="Current password"
                />
              )}
              <div className="flex gap-3">
                <input
                  data-testid="mfa-disable-code"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className={`${inputCls} w-40 text-center font-mono text-xl tracking-[0.4em]`}
                  placeholder="000000"
                />
                <button
                  data-testid="mfa-disable-btn"
                  onClick={disable}
                  disabled={busy || code.length !== 6}
                  className="flex items-center gap-2 border border-red-400/60 px-6 py-3 text-sm font-semibold text-red-300 transition-colors hover:bg-red-500/10 disabled:opacity-60"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Disable"}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ForceChangePassword({ onDone }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("Passwords do not match.");
      return;
    }
    setSaving(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated. Welcome to your portal.");
      await onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-md" data-testid="force-change-password">
      <div className="border border-steel-border bg-steel-surface p-8">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-steel-border">
            <Lock className="h-5 w-5 text-steel-brass" />
          </div>
          <div>
            <h3 className="font-display text-xl font-medium text-steel-text">Set your password</h3>
            <p className="text-xs text-steel-muted">Choose a new password to secure your account.</p>
          </div>
        </div>
        <p className="mt-5 text-sm leading-relaxed text-steel-secondary">
          Your account was created with a temporary password. For your security, please set a new one before continuing.
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Temporary password</label>
            <input data-testid="fcp-current" type="password" className={inputCls} value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="From your welcome email" autoComplete="current-password" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">New password</label>
            <input data-testid="fcp-new" type="password" className={inputCls} value={next} onChange={(e) => setNext(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
          </div>
          <div>
            <label className="mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">Confirm new password</label>
            <input data-testid="fcp-confirm" type="password" className={inputCls} value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password" autoComplete="new-password" />
          </div>
          <button
            type="submit"
            disabled={saving}
            data-testid="fcp-submit"
            className="flex w-full items-center justify-center gap-2 border border-steel-brass bg-steel-brass/10 px-6 py-3.5 text-sm font-medium text-steel-text transition-colors hover:bg-steel-brass/20 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
            {saving ? "Saving…" : "Set password & continue"}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Portal() {
  const { user, setUser, logout, checkAuth } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.user) setUser(location.state.user);
  }, [location.state, setUser]);

  if (user && user.role === "admin") {
    return <Navigate to="/admin" replace />;
  }

  return (
    <div data-testid="portal-page" className="pt-20">
      <section className="border-b border-steel-border">
        <div className="mx-auto flex max-w-7xl flex-col gap-6 px-6 py-24 lg:flex-row lg:items-end lg:justify-between lg:px-10 lg:py-32">
          <SectionHead
            overline="Client Portal"
            title={user ? `Welcome back, ${user.name?.split(" ")[0] || "client"}.` : "Your documents. Protected."}
            copy={user ? "Upload documents, manage your files, and track what we still need." : "The secure portal is where sensitive documents live — encrypted transmission, controlled access."}
          />
          {user && (
            <button
              data-testid="portal-logout-btn"
              onClick={logout}
              className="flex shrink-0 items-center gap-2 border border-steel-border px-5 py-2.5 text-sm text-steel-secondary transition-colors hover:border-red-400 hover:text-red-400"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </button>
          )}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        {user === null ? (
          <div className="flex items-center gap-3 text-steel-muted" data-testid="portal-loading">
            <Loader2 className="h-5 w-5 animate-spin text-steel-brass" />
            <span className="font-mono text-xs uppercase tracking-[0.25em]">Checking session…</span>
          </div>
        ) : user === false ? (
          <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
            <div className="lg:col-span-6">
              <AuthView />
            </div>
            <div className="lg:col-span-6">
              <Reveal delay={0.1}>
                <div className="flex gap-5 border border-steel-border bg-steel-surface p-7">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center border border-steel-border">
                    <ShieldCheck className="h-5 w-5 text-steel-brass" />
                  </div>
                  <div>
                    <h3 className="font-display text-lg font-medium text-steel-text">Why an account?</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-steel-secondary">
                      Your tax documents contain your most sensitive financial information. The portal keeps
                      them encrypted in transit, tied to your account, and accessible only to you and your
                      preparer — never floating around in email inboxes.
                    </p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        ) : (
          user.must_change_password ? (
            <ForceChangePassword onDone={checkAuth} />
          ) : !user.details_confirmed ? (
            <ConfirmDetails onDone={checkAuth} />
          ) : (
            <Dashboard user={user} />
          )
        )}
      </section>
    </div>
  );
}
